import type { TableProps } from 'antd';

import type { Option } from '@/utils/types';

import type { ColumnOverrides, GroupColumn, ToggleableColumn } from './types';

const getToggleableColumns = (
  columns: TableProps['columns']
): ToggleableColumn[] => {
  const cols = columns ?? [];

  return cols.flatMap(col => {
    const group = col as GroupColumn;
    if (group.children?.length) {
      return group.children.filter(child => child.toggleable !== false);
    }
    return [col];
  });
};

export const parseColumns = (
  columns: TableProps['columns']
): { options: Option[]; value: string[] } => {
  const toggleable = getToggleableColumns(columns);

  const options = toggleable.map(
    col => ({ label: col.title, value: col.key }) as Option
  );
  const value = toggleable
    .filter(col => !col.hidden)
    .map(col => col.key as string);

  return { options, value };
};

/**
 * The columns whose checked state the user has moved away from what is
 * applied, keyed by column key.
 *
 * Only differences are recorded, so the selection is a small statement of
 * intent rather than a snapshot: it stays valid while the option list changes
 * underneath (a refetch, a page change, a column appearing), and a column the
 * user never touched keeps following whatever is applied to it.
 */
export const buildOverrides = (
  options: Option[],
  visible: string[],
  checked: Set<string>
): ColumnOverrides => {
  const visibleKeys = new Set(visible);
  const overrides: ColumnOverrides = {};

  options.forEach(option => {
    const isChecked = checked.has(option.value);

    if (isChecked !== visibleKeys.has(option.value)) {
      overrides[option.value] = isChecked;
    }
  });

  return overrides;
};

/**
 * The keys to render as checked: the user's explicit toggle for a column when
 * there is one, otherwise the applied visibility.
 */
export const resolveCheckedKeys = (
  options: Option[],
  visible: string[],
  overrides: ColumnOverrides
): string[] => {
  const visibleKeys = new Set(visible);

  return options
    .map(option => option.value)
    .filter(key => overrides[key] ?? visibleKeys.has(key));
};

/**
 * Whether a resolved selection still differs from what is applied.
 *
 * This is the question Apply answers, so it is asked of the resolved keys
 * rather than of the overrides: an override stops mattering once the column
 * disappears or the applied visibility catches up with it, and holding the
 * button open on a selection with nothing left to say would write the state
 * that is already in place.
 */
export const hasSelectionChanged = (
  checked: string[],
  visible: string[]
): boolean => {
  if (checked.length !== visible.length) return true;

  const visibleKeys = new Set(visible);

  return checked.some(key => !visibleKeys.has(key));
};
